import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Search,
  UserCheck,
  Home,
  Scissors,
  HandMetal,
  Eye,
  Palette,
  Heart,
  ShieldCheck,
  CreditCard,
  MapPin,
  ArrowRight,
  Hammer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const SERVICES = [
  { name: 'Haircut + Beard', icon: Scissors, accent: 'bg-slate-900' },
  { name: 'Nails', icon: HandMetal, accent: 'bg-rose-500' },
  { name: 'Brows & Lashes', icon: Eye, accent: 'bg-violet-600' },
  { name: 'Makeup', icon: Palette, accent: 'bg-pink-500' },
  { name: 'Massage', icon: Heart, accent: 'bg-emerald-600' },
];

const STEPS = [
  { num: '1', title: 'Choose your service', body: 'Pick a category — barber, nails, beauty, makeup or massage.', icon: Search },
  { num: '2', title: 'Pick a verified pro', body: 'Browse approved local professionals near you in Harrow.', icon: UserCheck },
  { num: '3', title: 'They come to your home', body: 'Relax — your pro arrives at the time you booked.', icon: Home },
];

const TRUST_POINTS = [
  { icon: ShieldCheck, label: 'Verified professionals' },
  { icon: CreditCard, label: 'Secure Stripe payments' },
  { icon: MapPin, label: 'Harrow local launch' },
  { icon: Hammer, label: 'Cleanup included for grooming' },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">BeautyMateUK</span>
          </div>
          <nav className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/customer/login')} data-testid="nav-customer-login">
              Sign in
            </Button>
            <Button
              size="sm"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              onClick={() => navigate('/customer')}
              data-testid="nav-book-now"
            >
              Book now
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-rose-200/40 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-violet-200/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700">
              <MapPin className="h-3 w-3" /> Launching first in Harrow
            </span>
            <h1 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              Beauty &amp; grooming services <span className="text-rose-600">at your home in Harrow</span>
            </h1>
            <p className="mt-4 text-slate-600 text-base lg:text-lg max-w-xl">
              Book verified local barbers, nail artists, beauticians, makeup artists, and massage therapists. Secure payment, trusted partners, and cleanup included for grooming services.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12"
                onClick={() => navigate('/customer')}
                data-testid="cta-book-now"
              >
                Book now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="rounded-full px-8 h-12 border-slate-300"
                onClick={() => navigate('/partner')}
                data-testid="cta-become-partner"
              >
                Become a Partner
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {TRUST_POINTS.slice(0, 2).map((t) => (
                <span key={t.label} className="inline-flex items-center gap-1">
                  <t.icon className="h-3.5 w-3.5 text-emerald-600" /> {t.label}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 shadow-2xl p-6 lg:p-8 text-white relative overflow-hidden">
              <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-rose-500/30 blur-2xl" />
              <p className="text-xs uppercase tracking-widest text-rose-200">Today's pick</p>
              <h3 className="mt-2 text-2xl font-bold">Haircut + Beard</h3>
              <p className="text-slate-300 text-sm mt-1">from £35 · at your home</p>
              <div className="mt-6 space-y-2 text-sm">
                <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-rose-300" /> Verified barber in HA1</p>
                <p className="flex items-center gap-2"><Hammer className="h-4 w-4 text-rose-300" /> Cleanup included</p>
                <p className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-rose-300" /> Pay securely with Stripe</p>
              </div>
              <Button
                onClick={() => navigate('/customer')}
                className="mt-6 w-full rounded-full bg-white text-slate-900 hover:bg-slate-100"
                data-testid="hero-card-cta"
              >
                Find services near you
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <p className="text-xs uppercase tracking-widest text-rose-600 text-center">How it works</p>
        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 text-center mt-1">Three taps to your home appointment</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {STEPS.map((s) => (
            <div key={s.num} className="rounded-2xl border border-slate-200 p-5 bg-white">
              <div className="flex items-center justify-between">
                <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center font-bold">
                  {s.num}
                </div>
                <s.icon className="h-4 w-4 text-slate-400" />
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Popular services */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <p className="text-xs uppercase tracking-widest text-rose-600 text-center">Popular services</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 text-center mt-1">Something for everyone</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            {SERVICES.map((s) => (
              <button
                key={s.name}
                onClick={() => navigate('/customer')}
                data-testid={`landing-service-${s.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="group rounded-2xl bg-white border border-slate-200 p-4 hover:border-rose-300 hover:shadow-md transition-all text-left"
              >
                <div className={`h-10 w-10 rounded-xl ${s.accent} flex items-center justify-center text-white`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <p className="mt-3 font-semibold text-sm text-slate-900">{s.name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Book at home</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <div className="grid md:grid-cols-4 gap-3">
          {TRUST_POINTS.map((t) => (
            <div key={t.label} className="rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <t.icon className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-900">{t.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 pb-14">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 lg:p-12 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold">Ready when you are</h2>
          <p className="mt-2 text-slate-300">Find a verified beauty pro near you in Harrow.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="rounded-full px-8 h-12 bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => navigate('/customer')}
              data-testid="cta-book-now-bottom"
            >
              Book Now
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 h-12 border-slate-600 text-slate-900 hover:bg-slate-100"
              onClick={() => navigate('/partner')}
              data-testid="cta-become-partner-bottom"
            >
              Become a Partner
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
              <Sparkles className="h-3 w-3 text-white" />
            </div>
            <span>© {new Date().getFullYear()} BeautyMateUK Ltd.</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/customer" className="hover:text-slate-900">Customer App</Link>
            <Link to="/partner" className="hover:text-slate-900">Partner App</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/partner-terms" data-testid="footer-partner-terms" className="hover:text-slate-900">Partner Terms</Link>
            <Link to="/safety-policy" data-testid="footer-safety" className="hover:text-slate-900">Safety</Link>
            <Link to="/admin" className="text-slate-400 hover:text-slate-700" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
